import { DBSchema, IDBPDatabase, openDB } from "idb";

import { CatalogData, CatalogKey } from "./CatalogStorage.types";

const DB_NAME = "catalog-storage";
const DB_VERSION = 1;

const STORE_DATA = "catalog-data" as const;
const STORE_META = "catalog-meta" as const;

interface CatalogDbSchema extends DBSchema {
    [STORE_DATA]: {
        key: CatalogKey;
        value: CatalogData[CatalogKey];
    };
    [STORE_META]: {
        key: CatalogKey;
        value: { version: string };
    };
}

type CatalogMeta = Record<CatalogKey, { version: string }>;

export class CatalogStorageDb {
    private _db: IDBPDatabase<CatalogDbSchema> | null = null;

    private async db(): Promise<IDBPDatabase<CatalogDbSchema>> {
        if (this._db) return this._db;

        this._db = await openDB<CatalogDbSchema>(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_DATA)) {
                    db.createObjectStore(STORE_DATA);
                }
                if (!db.objectStoreNames.contains(STORE_META)) {
                    db.createObjectStore(STORE_META);
                }
            },
        });

        return this._db;
    }

    async write<K extends CatalogKey>(
        key: K,
        data: CatalogData[K],
    ): Promise<void> {
        const db = await this.db();
        await db.put(STORE_DATA, data, key);
    }

    async read<K extends CatalogKey>(key: K): Promise<CatalogData[K] | null> {
        const db = await this.db();
        return (await db.get(STORE_DATA, key)) ?? null;
    }

    async delete(key: CatalogKey): Promise<void> {
        const db = await this.db();
        await db.delete(STORE_DATA, key);
        await db.delete(STORE_META, key);
    }

    async saveMeta(key: CatalogKey, version: string): Promise<void> {
        const db = await this.db();
        await db.put(STORE_META, { version }, key);
    }

    async getMeta(): Promise<CatalogMeta | null> {
        const db = await this.db();
        const keys = await db.getAllKeys(STORE_META);
        if (keys.length === 0) return null;

        const entries = await Promise.all(
            keys.map(async (k) => {
                const val = await db.get(STORE_META, k);
                return [k, val] as const;
            }),
        );

        return Object.fromEntries(entries) as CatalogMeta;
    }

    async getVersion(key: CatalogKey): Promise<string | null> {
        const db = await this.db();
        const meta = await db.get(STORE_META, key);
        return meta?.version ?? null;
    }

    async close(): Promise<void> {
        this._db?.close();
        this._db = null;
    }
}
